import { reactive } from 'vue';

/**
 * App-wide confirm dialog service — a single in-app replacement for window.confirm().
 *
 * Usage (anywhere, incl. plain .js):
 *   import { useConfirm } from '@/composable/useConfirm';
 *   const { confirm } = useConfirm();
 *   if (await confirm({ title: 'Add scan to queue?', message: '…', confirmText: 'Add to queue' })) {
 *     // user confirmed
 *   }
 *
 * A single <ConfirmDialog> is mounted once in AppLayout and bound to this shared state via
 * `confirmState` + the resolve helpers below. Every confirm() returns a Promise<boolean>.
 */
const confirmState = reactive({
  visible: false,
  options: {},
  _resolve: null,
});

const DEFAULTS = {
  title: 'Are you sure?',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  danger: false,
  icon: 'mdi-help-circle-outline',
};

function confirm(options = {}) {
  // Resolve any dialog already open (shouldn't normally happen) as cancelled before opening a new one.
  if (confirmState._resolve) {
    confirmState._resolve(false);
    confirmState._resolve = null;
  }
  confirmState.options = { ...DEFAULTS, ...options };
  return new Promise((resolve) => {
    confirmState._resolve = resolve;
    confirmState.visible = true;
  });
}

// Called by the host dialog. `result` = true (confirmed) or false (cancelled/dismissed).
function settleConfirm(result) {
  confirmState.visible = false;
  if (confirmState._resolve) {
    confirmState._resolve(result);
    confirmState._resolve = null;
  }
}

export function useConfirm() {
  return { confirm, confirmState, settleConfirm };
}
